import React, { Component } from 'react';
import { connect } from 'react-redux'
import './App.css';
import LoginScreen from './screens/LoginScreen';
import ForceChangePassword from './screens/ForceChangePassword';
import AdminScreen from './screens/AdminScreen';
import TransactionMonitor from './screens/TransactionMonitor';
import AppShell from './components/AppShell';
import { ROLE_LABEL } from './roleLabels';
import { deviceDetect, isMobile, deviceType, mobileVendor, mobileModel } from 'react-device-detect';
import {
  login,
  logout,
  setOnsiteState,
  setEventState,
  setEventList,
  resetEventState,
  setAdminState,
  setAdminControl,
  getCustomers,
  getSites,
  getGates,
  getOnsiteCount,
  getUsers,
  getSubscribers,
  getEvents,
} from './slices'

import packageData from '../package.json';

const apiURL = process.env.REACT_APP_BACKEND
const version = packageData.version

// Presentational role labels now live in ./roleLabels (shared: header, Users, User Log).
// Nav key ↔ Redux showAdminControl mapping. Non-entity views (monitor/onsite/
// display) all resolve to the live TransactionMonitor (showAdminControl===false).
const CONTROL_TO_NAV = { subscriber: 'customer', customer: 'customer', site: 'customer', gate: 'gate', user: 'user', assignment: 'assignment', company: 'company', person: 'person', lpn: 'lpn' }
const NAV_TITLE = { monitor: 'Event Monitor', onsite: 'On-Site', display: 'Display', gate: 'Organization', user: 'Organization', assignment: 'Organization', company: 'Companies', lpn: 'License Plates', person: 'People', customer: 'Organization' }

export class App extends Component {

  constructor(props) {
    super(props)
    // Presentational nav highlight only. The MOUNTED screen is still decided by
    // Redux showAdminControl (untouched); this just lets Event Monitor / On-Site
    // / Display each stay lit even though they all mount the monitor screen.
    this.state = { activeNav: props.showAdminControl ? (CONTROL_TO_NAV[props.showAdminControl] || 'monitor') : 'monitor' }
  }

  componentDidMount() {
    // [86bbwu3px B-ROOT-1] Always refetch live data on mount when logged in — do NOT gate on the
    // PERSISTED siteLoaded flag. A hard refresh rehydrates siteLoaded=true, so the old condition
    // skipped getInitialData and events/onsite/admin lists stayed stale until a full logout+login
    // (the only path that force-calls getInitialData). Refetching on every mount clears items
    // 7,9,10,11 and part of 6. (Dev <StrictMode> double-mount masked this; the prod single mount
    // rehydrates siteLoaded=true before mount and exhibited it — hence testing on the prod build.)
    if (this.props.isLoggedIn) {
      this.getInitialData()
    };
  };

  componentDidUpdate = async (prevProps, prevState) => {
    // [86bbwu3px] Live polling (the crossings feed AND the on-site counts) is now owned by a single
    // useLivePoll loop inside TransactionMonitor — pausing on the User Log view / catching up on tab
    // return / surviving errors are all handled there. App no longer schedules or cancels any poll
    // timer here (the old eventListLoaded→setTimeout chain was the fragile part that could silently
    // die on a CSV export or a hidden-tab throttle).
    // When a setup pane closes itself (its Done button, the back caret, or any direct
    // setAdminControl(false)), the mounted screen returns to the Event Monitor. The rail
    // highlight + page title are LOCAL state, so keep them in sync with that transition.
    if (prevProps.showAdminControl && !this.props.showAdminControl && this.state.activeNav !== 'monitor') {
      this.setState({ activeNav: 'monitor' })
    }
  }

  getInitialData = async () => {
    await this.props.resetEventState()
    await this.props.getEvents({filters: {page: 1}})
    await this.props.getGates()
    this.props.getCustomers()
    this.props.getSites()
    this.props.getUsers()
    // The subscriber list backs the Super-only Event Monitor / User Log / Export "All subscribers"
    // filter. The thunk self-guards: Super (100) gets every tenant, Account Admin (1) only its own
    // (backend-scoped), and every other role skips the request — mirrors how getCustomers scopes itself.
    this.props.getSubscribers()

    // get onsite count of the first gate
    const gates = this.props.gates
    const initialGate = !this.props.onsiteGateId && gates && gates[0] && gates[0].id ? gates[0].id : this.props.onsiteGateId
    await this.props.setOnsiteState({field: 'onsiteGateId', value: initialGate})
    this.props.getOnsiteCount()
  };

  setUserData = async (data) => {
    // set user data upon successful login
    this.setEntryInGateLog(data, 'LOGIN');
    await this.props.login(data)
    this.getInitialData()
  };

  setEntryInGateLog = async (data, type) => {
    // add the login /logout event entry into our db
    const date = new Date();
    const today = date.toLocaleString();
    let device = deviceDetect();
    let sDevice = isMobile
                  ? `${deviceType}: ${mobileVendor} ${mobileModel} - ${device.osName} ${device.osVersion} - ${device.browserName} ${device.browserFullVersion}`
                  : `computer: ${device.osName} ${device.osVersion} - ${device.browserName} ${device.browserFullVersion}`

    fetch( apiURL + 'api/gatelog', {
        method: 'POST',
        headers: {
        'Content-Type': 'application/json',
        // [86bbumedw] P0: /api/gatelog now requires a token. At LOGIN the webToken prop may not
        // be in Redux yet (state-propagation race) — use the token straight from the login
        // payload; fall back to the stored webToken (e.g. at LOGOUT, where data has no token).
        'Authorization': data.token ? ('JWT ' + data.token) : this.props.webToken
        },
        body: JSON.stringify({
            GateUserId: data.id,
            GateAssignmentId: data.gateAssignmentId,
            GateAclId: data.gateAclId,
            GateSubscriberId: data.subscriberId,
            GateCustomerId: data.customerId,
            sType: type,
            sDevice: sDevice,
            dTimestamp: today,
        })
      })
      .then(function(response) {
          if (!response.ok) {
              throw Error(response.statusText);
          };
          return response;
      })
      .then(response => response.json())
      .then( data => {
          // console.log('log entry updated.')
      })
      .catch(error => {
          console.error('Error:', error);
      });
  };

  setOnsiteCountId = async (id) => {
    const field = this.props.getOnsiteCountBySite ? 'onsiteSiteId' : 'onsiteGateId'
    await this.props.setOnsiteState({field: field, value: id})
    this.props.getOnsiteCount()
  };

  setOnsiteCountBySite = async (value) => {
    await this.props.setOnsiteState({field: 'getOnsiteCountBySite', value: value})
    this.props.getOnsiteCount()
  };

  // Sidebar → existing screen-switch mechanism. `controlKey` is the value the
  // real app already uses for state.admin.showAdminControl (false = monitor).
  handleSelectScreen = (controlKey) => {
    // Defer the (heavier) screen swap one tick so the nav highlight — set first via
    // handleSelectNav — paints immediately. Otherwise the clicked pill only lights up
    // once the new pane's data finishes loading (~2s against prod), which read as lag.
    setTimeout(() => this.props.setAdminControl(controlKey), 0)
  }

  // Presentational nav highlight (monitor/onsite/display all mount the monitor).
  handleSelectNav = (navKey) => {
    this.setState({ activeNav: navKey })
  }

  doLogout = async () => {
    let data = {
      id: parseInt(this.props.userId),
      gateAclId: this.props.authId,
      gateAssignmentId: this.props.gateAssignmentId,
      subscriberId: this.props.subscriberId,
      customerId: this.props.customerId
    }
    this.setEntryInGateLog(data, 'LOGOUT')
    fetch( apiURL + 'logout/' + this.props.userId )

    await this.props.logout()
  }

  // Build the props the presentational shell needs — pure derivation from the
  // existing Redux auth/site/gate state (no new state, no writes).
  buildShellProps = () => {
    const { authId, firstName, lastName, customerName, accountType, siteId, gateId, sites, gates, showAdminControl, backendVersion } = this.props
    const isAdmin = (authId === 100 || authId < 3)

    // location context: customerName is persisted; site/gate names are looked up
    // from the already-loaded lists by the stored ids (reuse, no extra fetch).
    const site = (sites || []).find(s => s && s.id === siteId)
    const gate = (gates || []).find(g => g && g.id === gateId)
    // Header context shows only for SCOPED users (a specific customer › site › gate).
    // For super/master (who span everything) we deliberately show NO scope pill — the
    // events table's own customer filter is the single source of scope, so a header
    // "All customers/subscribers" label just read as a confusing duplicate of it.
    const location = {
      customerName: (authId === 100 || authId === 1) ? '' : (customerName && customerName !== 'Customer N/A' ? customerName : ''),
      subscriberLabel: '',
      siteName: (authId === 100 || authId === 1) ? '' : (site ? (site.sName || site.name) : ''),
      gateName: (authId === 100 || authId === 1) ? '' : (gate ? (gate.sName || gate.name) : ''),
    }

    const name = [firstName, lastName].filter(Boolean).join(' ').trim() || 'User'
    const initials = (name === 'User')
      ? '—'
      : ([firstName, lastName].filter(Boolean).map(s => s.charAt(0).toUpperCase()).join('') || name.charAt(0).toUpperCase())

    const navKey = this.state.activeNav
    return {
      role: { isAdmin, accountType: accountType || 'agency', gateAclId: authId },
      activeControl: showAdminControl,
      activeNav: navKey,
      onSelectScreen: this.handleSelectScreen,
      onSelectNav: this.handleSelectNav,
      location,
      user: { name, roleLabel: ROLE_LABEL[authId] || '', initials },
      version: { app: version, backend: backendVersion },
      onSignOut: this.doLogout,
      onsiteCount: this.props.currentPeopleOnsite,
      pageTitle: NAV_TITLE[navKey] || 'Event Monitor',
    }
  }

  render() {
    // force user to logout if they don't have the latest redux states
    // on every push, change this condition with one of the latest redux state
    // [86bbumedw] bumped to `changePassword` (added this release in AuthSlice initialState = null),
    // so a store persisted BEFORE this release (lacking that key → undefined) is force-logged-out and
    // can't crash on the new slice state; fresh/current stores have it defined (null) and are kept.
    if (this.props.changePassword === undefined) {
      this.props.logout()
      return
    }

    if (!this.props.isLoggedIn) {
      return (
        <div className="App">
          <LoginScreen setUserData={ this.setUserData } />
        </div>
      )
    }

    // [86bbumedw] forced password change after a temp-password create/reset: block the
    // console until the user sets their own password (the screen clears the flag on success).
    if (this.props.changePassword === 'true') {
      return (
        <div className="App">
          <ForceChangePassword />
        </div>
      )
    }

    return (
      <div className="App">
        <AppShell { ...this.buildShellProps() }>
          {/* The mounted screen is still chosen by Redux showAdminControl:
              an entity control when set, else the live TransactionMonitor.
              Both are wrapped in the shell's ErrorBoundary. */}
          { this.props.showAdminControl
              ? <AdminScreen />
              : <TransactionMonitor setOnsiteCountBySite={this.setOnsiteCountBySite} setOnsiteCountId={this.setOnsiteCountId} />
          }
        </AppShell>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    isLoggedIn: state.auth.isLoggedIn,
    webToken: state.auth.webToken,
    changePassword: state.auth.changePassword,
    authId: state.auth.authId,
    userId: state.auth.userId,
    firstName: state.auth.firstName,
    lastName: state.auth.lastName,
    customerName: state.auth.customerName,
    subscriberName: state.auth.subscriberName,
    accountType: state.auth.accountType,
    subscriberId: state.auth.subscriberId,
    customerId: state.auth.customerId,
    siteId: state.auth.siteId,
    gateId: state.auth.gateId,
    gateAssignmentId: state.auth.gateAssignmentId,
    backendVersion: state.auth.backendVersion,
    getOnsiteCountBySite: state.onsite.getOnsiteCountBySite,
    onsiteGateId: state.onsite.onsiteGateId,
    onsiteSiteId: state.onsite.onsiteSiteId,
    currentPeopleOnsite: state.onsite.currentPeopleOnsite,
    monitorView: state.event.monitorView,
    eventDownloadError: state.event.eventDownloadError,
    showAdminControl: state.admin.showAdminControl,
    gates: state.gate.gates,
    sites: state.site.sites,
    siteLoaded: state.site.siteLoaded,
  }
}
export default connect(mapStateToProps, {
  login,
  logout,
  setOnsiteState,
  setEventState,
  setEventList,
  resetEventState,
  setAdminState,
  setAdminControl,
  getCustomers,
  getSites,
  getGates,
  getOnsiteCount,
  getUsers,
  getSubscribers,
  getEvents,
})(App)
